// Template201: Bangkok - Ayutthaya (página 2). Idéntico a PageAyutthaya2.jsx. borderRadius: "12px".

import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";

const Template201 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [mapLoaded, setMapLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const { setMapFullscreen } = useMapFullscreen() || {};

	const headerText = page?.texts?.[0]?.content ?? "Extra: Ayutthaya";
	const tipText = page?.texts?.[1]?.content;
	const watYaiTitle = page?.texts?.[2]?.content ?? "Wat Yai Chai Mongkhon";
	const watYaiBody = page?.texts?.[3]?.content;
	const watChaiTitle = page?.texts?.[4]?.content ?? "Wat Chaiwatthanaram";
	const watChaiBody = page?.texts?.[5]?.content;
	const otrosTemplosText = page?.texts?.[6]?.content;
	const mapEmbedUrl = page?.data?.mapEmbedUrl;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found?.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const watYaiImage = getImageSrc("wat-yai");
	const watChaiImage = getImageSrc("wat-chai");
	const stickerUrl = getCanvasImageUrl("sticker");
	const logoImage = getCanvasImageUrl("logo");

	const sabiasQue = Array.isArray(page?.data?.sabiasQue)
		? page.data.sabiasQue
		: [];
	const actualHeaderColor = headerBackgroundColor || "#E8A838";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: 0,
	};

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `@keyframes template201-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	if (fullscreen && mapEmbedUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#E8A838",
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: "#E8A838",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande Ayutthaya"
					src={mapEmbedUrl}
					width="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					minHeight: 400,
					marginTop: "-70px",
					position: "relative",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{stickerUrl && (
					<Box
						component="img"
						src={stickerUrl}
						alt=""
						sx={{ width: 120, position: "absolute", bottom: -20, left: -20 }}
					/>
				)}
				{tipText && (
					<Box
						sx={{
							position: "absolute",
							bottom: isSmallScreen ? 80 : 60,
							left: isSmallScreen ? 6 : 40,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							color: "#444",
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 120,
						}}
					>
						{tipText}
					</Box>
				)}
				{mapEmbedUrl && !mapLoaded && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #E8A838",
								borderRadius: "50%",
								animation: "template201-spin 1s linear infinite",
							}}
						/>
					</Box>
				)}
				{mapEmbedUrl && (
					<>
						<Box
							component="button"
							onClick={() => setFullscreen(true)}
							sx={{
								position: "absolute",
								top: 80,
								right: 10,
								backgroundColor: "#E8A838",
								color: "#fff",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
								zIndex: 4,
								border: "none",
								cursor: "pointer",
							}}
						>
							Ver mapa grande
						</Box>
						<Box
							component="iframe"
							title="Mapa Ayutthaya"
							src={mapEmbedUrl}
							loading="lazy"
							sx={{ border: 0, flex: 1, minHeight: 400, width: "100%" }}
							onLoad={() => setMapLoaded(true)}
						/>
					</>
				)}
				{!mapEmbedUrl && (
					<Box
						sx={{
							flex: 1,
							minHeight: 400,
							width: "100%",
							backgroundColor: "#f0f0f0",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 13,
							color: "#666",
							padding: 2,
							textAlign: "center",
						}}
					>
						Conecta a internet para ver el mapa de la zona.
					</Box>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					display: "flex",
					gap: 2,
					padding: "0 12px",
					marginBottom: 1.75,
					marginTop: 1.25,
					position: "relative",
				}}
			>
				{watYaiImage && (
					<Box
						component="img"
						src={watYaiImage}
						alt=""
						sx={{
							width: "40%",
							height: 100,
							objectFit: "cover",
							borderRadius: "12px",
						}}
					/>
				)}
				<Box sx={{ width: "60%" }}>
					<Box component="h3" sx={h3}>
						{watYaiTitle}
					</Box>
					{watYaiBody && (
						<Box component="p" sx={p}>
							{watYaiBody}
						</Box>
					)}
				</Box>
				{sabiasQue.length > 0 && (
					<Box sx={{ position: "absolute", top: 10, left: "35%" }}>
						<SabiasQue
							icono={sabiasQue[0]?.icono ?? "🛕"}
							texto={sabiasQue[0]?.texto ?? ""}
						/>
					</Box>
				)}
			</Box>

			<Box
				data-block-id="text-2"
				sx={{
					display: "flex",
					gap: 2,
					padding: "0 12px",
					marginBottom: 1.75,
					position: "relative",
				}}
			>
				{watChaiImage && (
					<Box
						component="img"
						src={watChaiImage}
						alt=""
						sx={{
							width: "40%",
							height: 100,
							objectFit: "cover",
							borderRadius: "12px",
						}}
					/>
				)}
				<Box sx={{ width: "60%" }}>
					<Box component="h3" sx={h3}>
						{watChaiTitle}
					</Box>
					{watChaiBody && (
						<Box component="p" sx={p}>
							{watChaiBody}
						</Box>
					)}
				</Box>
				{sabiasQue.length > 1 && (
					<Box sx={{ position: "absolute", top: 10, left: "35%" }}>
						<SabiasQue
							icono={sabiasQue[1]?.icono ?? "🌅"}
							texto={sabiasQue[1]?.texto ?? ""}
						/>
					</Box>
				)}
			</Box>

			{otrosTemplosText && (
				<Box
					data-block-id="text-3"
					sx={{
						padding: "0 12px",
						marginBottom: 2,
						paddingBottom: isSmallScreen ? 2 : 0,
					}}
				>
					<Box
						sx={{
							backgroundColor: "#d4edda",
							border: "2px solid #28a745",
							borderRadius: "12px",
							padding: "12px 16px",
							color: "#333",
						}}
					>
						<Box
							component="p"
							sx={{ ...p, margin: 0 }}
							dangerouslySetInnerHTML={{ __html: otrosTemplosText }}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template201.thumbnail = "/images/template-1.png";
export default Template201;
